import React, { useState, FormEvent } from 'react';
import { useLocation, Navigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthProvider';
import { useTheme } from '../contexts/ThemeProvider';
import logoWhite from '../assets/logo-repro-white.svg';
import logoDark from '../assets/logo-repro-v3-dark.svg';

export default function Login() {
  const { login, authState } = useAuth();
  const { resolvedTheme } = useTheme();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard';

  if (authState === 'authenticated') return <Navigate to={from} replace />;
  if (authState === 'needs_mfa_verify') return <Navigate to="/mfa-verify" replace />;
  if (authState === 'needs_mfa_setup') return <Navigate to="/mfa-setup" replace />;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const logo = resolvedTheme === 'dark' ? logoWhite : logoDark;

  return (
    <div className="min-h-screen flex items-center justify-center bg-base-100 px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src={logo} alt="RePro" className="h-8 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-base-content">Admin Portal</h1>
          <p className="text-sm text-base-content/60 mt-1">Sign in to continue</p>
        </div>

        <form onSubmit={handleSubmit} className="card bg-base-200 shadow-md">
          <div className="card-body gap-4">
            {error && <div className="alert alert-error text-sm">{error}</div>}

            <div className="form-control">
              <label className="label" htmlFor="email">
                <span className="label-text">Email</span>
              </label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input input-bordered w-full"
                placeholder="admin@reprostream.com"
              />
            </div>

            <div className="form-control">
              <label className="label" htmlFor="password">
                <span className="label-text">Password</span>
              </label>
              <input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input input-bordered w-full"
              />
              <label className="label">
                <Link to="/forgot-password" className="label-text-alt link link-hover text-primary">
                  Forgot password?
                </Link>
              </label>
            </div>

            <button type="submit" disabled={loading} className="btn btn-primary w-full mt-2">
              {loading ? <span className="loading loading-spinner loading-sm" /> : 'Sign in'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
